
import {
  computed, inject, defineComponent, onMounted, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDropZone } from '@vueuse/core';
import RefreshPull from 'pull-refresh-vue3';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '../store/driveStore';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import UsePullToRefresh from '@/composables/UsePullToRefresh';
import { DIRECTION } from '@/helpers/isScrollEnd';
import UsePagination from '@/composables/UsePagination';
// eslint-disable-next-line import/no-cycle
import DriveService from '@/modules/drive/services/DriveService';
import { IMixedData } from '@/types/interfaces';
import isPhoto from '@/helpers/fileDetector';
// eslint-disable-next-line import/no-cycle
import UploadService from '@/services/UploadService';
import { NAVIGATION_PAGES, UPLOAD_PLACES } from '@/types/enums';
import UseUpload from '@/composables/UseUpload';
import { convertBmpInFileList } from '@/helpers/bmpConvertor';
import DriveItems from '@/modules/drive/components/DriveItems.vue';
import DrivePhotos from '@/modules/drive/components/DrivePhotos.vue';
import DriveMixed from '@/modules/drive/components/DriveMixed.vue';
import AppNoMessage from '@/components/AppNoMessage.vue';
import DriveLoadBar from '@/modules/drive/components/UI/DriveLoadBar.vue';
import AppDropZone from '@/components/AppDropZone.vue';

export default defineComponent({
  components: {
    DriveItems,
    DrivePhotos,
    DriveMixed,
    RefreshPull,
    AppNoMessage,
    DriveLoadBar,
    AppDropZone,
  },
  emits: ['open-share'],
  props: {
    showSkeleton: { type: Boolean, default: false },
  },
  setup() {
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const router = useRouter();
    const route = useRoute();
    const { onUploadUpdate } = UseUpload();
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;

    const isNetwork = computed(() => route.meta.isNetwork);
    const currentPage = computed(() => route.meta.navigationPage);

    const {
      scroller, pageIdx, isPageLoading, onScroll, onCacheScroll, setLastPage,
    } = UsePagination(300);

    function refreshDrive(initial = false) {
      if (isNetwork.value) {
        driveStore.networkPhotos.page = 0;
        driveStore.networkPhotos.lastPage = -1;

        driveStore.networkCases.page = 0;
        driveStore.networkCases.lastPage = -1;

        driveStore.networkMixedData.page = 0;
        driveStore.networkMixedData.lastPage = -1;

        return Promise.all([
          driveStore.fetchNetworkCases(false, initial),
          driveStore.fetchNetworkPhotos(initial),
          driveStore.fetchNetworkMixedData(initial),
        ]);
      }

      driveStore.photos.page = 0;
      driveStore.photos.lastPage = -1;

      driveStore.cases.page = 0;
      driveStore.cases.lastPage = -1;

      driveStore.mixedData.page = 0;
      driveStore.mixedData.lastPage = -1;

      return Promise.all([
        driveStore.fetchCases(),
        driveStore.fetchPhotos(),
        driveStore.fetchMixedData(),
      ]);
    }

    const {
      loadingPull, loosingText, successText, pullingText, loadingText, onRefresh,
    } = UsePullToRefresh(refreshDrive);

    const casesRef = ref();
    const photosRef = ref();
    const mixedRef = ref();

    const driveCasesTitle = computed(() => (isNetwork.value ? $localize('drive.network.cases.title') : $localize('drive.section.cases.title')));

    const noDataMessage = computed(() => {
      const dataLoading = driveStore.dataLoading.cases || driveStore.dataLoading.photos || driveStore.dataLoading.mixed;
      const noData = !casesRef.value?.cases.length && !photosRef.value?.photos.length && !mixedRef.value?.mixedData.length;

      if (!dataLoading && noData) {
        if (driveStore.search) {
          return {
            text: $localize('no.search.results'),
            icon: 'icon-not-found',
          };
        }
        return {
          text: isNetwork.value ? $localize('nothing.shared') : $localize('no.items.added'),
          icon: 'icon-no-data',
        };
      }
      return '';
    });

    function setSearchValue() {
      if (route.query.search) {
        driveStore.search = route.query.search as string;
        router.replace({});
      } else {
        driveStore.search = '';
      }
    }

    const dataLoading = ref(false);

    const searchMixed = ref<IMixedData[]>([]);

    const mixedLastPage = computed(() => (isNetwork.value ? driveStore.networkMixedData.lastPage : driveStore.mixedData.lastPage));
    const mixedCurrentPage = computed(() => (isNetwork.value ? driveStore.networkMixedData.page : driveStore.mixedData.page));

    const mixedData = computed(() => {
      if (driveStore.search) {
        return searchMixed.value;
      }
      return (isNetwork.value ? driveStore.networkMixedData.data : driveStore.mixedData.data);
    });

    async function fetchCacheData() {
      if (isNetwork.value) {
        return driveStore.fetchNetworkMixedData();
      }
      return driveStore.fetchMixedData();
    }

    async function fetchSearchData() {
      const currentSearch = driveStore.search;
      const resolveRequest = () => {
        if (isNetwork.value) {
          return DriveService.getNetworkMixedData(pageIdx.value, driveStore.search);
        }
        return DriveService.getMixedData(pageIdx.value, driveStore.search);
      };

      return resolveRequest().then((response) => {
        if (currentSearch !== driveStore.search) {
          return Promise.reject(new Error('response is out of date'));
        }
        if (pageIdx.value === 0) {
          searchMixed.value = response.results.data.items;
        } else {
          searchMixed.value.push(...response.results.data.items);
        }
        return response;
      });
    }

    function handleScroll() {
      const direction = driveStore.isGridPhotoView ? DIRECTION.RIGHT : DIRECTION.BOTTOM;
      if (driveStore.search) {
        onScroll(fetchSearchData, direction, dataLoading.value);
      } else {
        onCacheScroll(fetchCacheData, direction, mixedLastPage.value, mixedCurrentPage.value);
      }
    }

    function removeFromSearch(id: string) {
      searchMixed.value = searchMixed.value.filter((item) => item.id !== id);
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      driveStore.dataLoading.mixed = true;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
            driveStore.dataLoading.mixed = false;
          });
      }, 1500);
    });

    function splitMedia(mediaList: File[]) {
      const fileList: File[] = [];
      const photoList: File[] = [];

      for (let i = 0; i < mediaList.length; i += 1) {
        if (isPhoto(mediaList[i])) {
          photoList.push(mediaList[i]);
        } else {
          fileList.push(mediaList[i]);
        }
      }

      return {
        fileList,
        photoList,
      };
    }

    async function onDrop(mediaList: File[] | null) {
      if (mediaList) {
        const { fileList, photoList } = splitMedia(mediaList);

        const newFileList = await convertBmpInFileList(fileList as unknown as FileList);
        const newPhotoList = await convertBmpInFileList(photoList as unknown as FileList);

        const fileUploads = UploadService.toFileUploads(newFileList);
        const photoUploads = UploadService.toFileUploads(newPhotoList);

        const folderId = currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER ? route.params.id as string : undefined;

        if (fileUploads.length) {
          UploadService.batchUploadTUS(fileUploads, onUploadUpdate, {
            location: folderId ? UPLOAD_PLACES.FOLDER : UPLOAD_PLACES.MIXED,
            id: folderId,
          }, folderId);
        }
        if (photoUploads.length) {
          UploadService.batchUploadTUS(photoUploads, onUploadUpdate, {
            location: folderId ? UPLOAD_PLACES.FOLDER_PHOTO : UPLOAD_PLACES.MIXED_PHOTO,
            id: folderId,
          }, folderId);
        }
      }
    }
    const dropZoneRef = ref<HTMLElement>();

    const { isOverDropZone } = useDropZone(dropZoneRef, onDrop);

    onMounted(() => {
      MonitoringService.sendPageMountedSpeed();
      setSearchValue();
      driveStore.resetCurrentCase();
      driveStore.folderFileAmount = [];
      if (isNetwork.value) {
        refreshDrive(true);
      }
    });

    return {
      isNetwork,
      casesRef,
      photosRef,
      mixedRef,
      driveStore,
      driveCasesTitle,
      noDataMessage,

      loadingPull,
      loosingText,
      successText,
      pullingText,
      loadingText,
      onRefresh,

      scroller,
      handleScroll,
      mixedData,
      isPageLoading,
      dataLoading,
      removeFromSearch,
      dropZoneRef,
      isOverDropZone,
      mainStore,
    };
  },
});
